<template>
  <div>
    <h2 class="text-2xl font-bold text-gray-900 mb-6">Create Account</h2>
    
    <form @submit.prevent="handleRegister" class="space-y-4">
      <!-- Name Fields -->
      <div class="grid grid-cols-2 gap-4">
        <div>
          <label for="firstName" class="label">First Name</label>
          <input
            id="firstName"
            v-model="form.first_name"
            type="text"
            required
            class="input"
            placeholder="John"
            :disabled="loading"
          />
        </div>
        <div>
          <label for="lastName" class="label">Last Name</label>
          <input
            id="lastName"
            v-model="form.last_name"
            type="text"
            required
            class="input"
            placeholder="Doe"
            :disabled="loading"
          />
        </div>
      </div>
      
      <!-- Username -->
      <div>
        <label for="username" class="label">Username</label>
        <input
          id="username"
          v-model="form.username"
          type="text"
          required
          class="input"
          placeholder="johndoe"
          :disabled="loading"
        />
      </div>
      
      <!-- Email -->
      <div>
        <label for="email" class="label">Email Address</label>
        <input
          id="email"
          v-model="form.email"
          type="email"
          required
          class="input"
          placeholder="you@example.com"
          :disabled="loading"
        />
      </div>
      
      <!-- Phone -->
      <div>
        <label for="phone" class="label">Phone Number</label>
        <input
          id="phone"
          v-model="form.phone"
          type="tel"
          class="input"
          placeholder="+1 (555) 123-4567"
          :disabled="loading"
        />
      </div>
      
      <!-- Password -->
      <div>
        <label for="password" class="label">Password</label>
        <input
          id="password"
          v-model="form.password"
          type="password"
          required
          class="input"
          placeholder="••••••••"
          :disabled="loading"
        />
        <p class="mt-1 text-xs text-gray-500">
          At least 8 characters with uppercase, lowercase, and numbers
        </p>
      </div>
      
      <!-- Confirm Password -->
      <div>
        <label for="confirmPassword" class="label">Confirm Password</label>
        <input
          id="confirmPassword"
          v-model="form.confirmPassword"
          type="password"
          required
          class="input"
          placeholder="••••••••"
          :disabled="loading"
        />
      </div>
      
      <!-- Terms -->
      <div>
        <label class="flex items-start">
          <input
            v-model="acceptTerms"
            type="checkbox"
            required
            class="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span class="ml-2 text-sm text-gray-600">
            I agree to the
            <a href="#" class="text-primary-600 hover:text-primary-500">Terms of Service</a>
            and
            <a href="#" class="text-primary-600 hover:text-primary-500">Privacy Policy</a>
          </span>
        </label>
      </div>
      
      <!-- Submit Button -->
      <button
        type="submit"
        :disabled="loading || !acceptTerms"
        class="w-full btn btn-primary flex items-center justify-center"
      >
        <span v-if="!loading">Create Account</span>
        <span v-else class="flex items-center">
          <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Creating account...
        </span>
      </button>
    </form>
    
    <!-- Sign In Link -->
    <div class="mt-6 text-center">
      <p class="text-sm text-gray-600">
        Already have an account?
        <RouterLink to="/login" class="font-medium text-primary-600 hover:text-primary-500">
          Sign in
        </RouterLink>
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { RouterLink } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useToast } from 'vue-toastification'

const authStore = useAuthStore()
const toast = useToast()

const form = ref({
  first_name: '',
  last_name: '',
  username: '',
  email: '',
  phone: '',
  password: '',
  confirmPassword: ''
})

const acceptTerms = ref(false)
const loading = ref(false)

async function handleRegister() {
  // Validate passwords match
  if (form.value.password !== form.value.confirmPassword) {
    toast.error('Passwords do not match')
    return
  }
  
  loading.value = true
  try {
    const { confirmPassword, ...registerData } = form.value
    await authStore.register(registerData)
  } catch (error) {
    // Error handled in store
  } finally {
    loading.value = false
  }
}
</script>